// TMOS Health Indicator Component
// Date: January 13, 2026
// Purpose: Display plan/objective health status with color coding

import React from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/outline';

export type HealthStatus = 'GREEN' | 'YELLOW' | 'RED';

export interface HealthIndicatorProps {
  status: HealthStatus;
  score?: number; // 0-100
  showScore?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const HealthIndicator: React.FC<HealthIndicatorProps> = ({
  status,
  score,
  showScore = false,
  size = 'md',
  className = ''
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  const textSizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  };

  const getStatusColor = () => {
    switch (status) {
      case 'GREEN':
        return 'text-[var(--color-success)]';
      case 'YELLOW':
        return 'text-[var(--color-warning)]';
      case 'RED':
        return 'text-[var(--color-error)]';
      default:
        return 'text-[var(--color-secondary-text)]';
    }
  };

  const getIcon = () => {
    switch (status) {
      case 'GREEN':
        return <CheckCircleIcon className={`${sizeClasses[size]} ${getStatusColor()}`} />;
      case 'YELLOW':
        return <ExclamationTriangleIcon className={`${sizeClasses[size]} ${getStatusColor()}`} />;
      case 'RED':
        return <XCircleIcon className={`${sizeClasses[size]} ${getStatusColor()}`} />;
      default:
        return null;
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {getIcon()}
      {showScore && score !== undefined && (
        <span className={`font-semibold ${textSizeClasses[size]} ${getStatusColor()}`}>
          {Math.round(score)}%
        </span>
      )}
    </div>
  );
};

